import React, { useEffect, useState } from "react";
import styles from "./UserEnquiriesComponent.module.css";
import axiosInstance from "../../configuration/api";
import { useAuth } from "../../context/AuthContext";
import { format } from "date-fns";
import { ClockLoader } from "react-spinners";

interface Product {
  _id: string;
  name: string;
  productId: string;
  picture?: {
    path: string;
    originalName: string;
  };
}

interface Series {
  _id: string;
  title: string;
  picture?: {
    path: string;
    originalName: string;
  };
}

interface EnquiryItem {
  productId: Product;
  seriesId: Series;
  color?: string;
  price: number;
  ourPrice: number;
  quantity: string;
}

interface Enquiry {
  _id: string;
  fullName: string;
  email: string;
  phone: string;
  status: string;
  createdAt: string;
  message: string;
  reply?: string;
  company?: string;
  enquiryItems: EnquiryItem[];
  pincode?: string;
  address?: string;
  updatedAt?: string;
}

interface ApiResponse {
  status: string;
  results: number;
  total: number;
  page: number;
  totalPages: number;
  data: {
    enquiries: Enquiry[];
  };
}

const UserEnquiriesComponent = () => {
  const { user } = useAuth();
  const userId = user?._id.toString();
  const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEnquiries, setTotalEnquiries] = useState(0);

  // Fetch enquiries from the API
  const fetchEnquiries = async (pageNum: number = 1) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      setError("");
      const baseUrl = process.env.REACT_APP_API_HOST;
      const response = await axiosInstance.get(
        `${baseUrl}/api/v1/enquiry/user/${userId}?page=${pageNum}&limit=5`
      );
      const data: ApiResponse = response.data;

      if (response.status === 200) {
        setEnquiries((prev) =>
          pageNum === 1
            ? data.data.enquiries
            : [...prev, ...data.data.enquiries]
        );
        setTotalPages(data.totalPages);
        setTotalEnquiries(data.total);
        setPage(pageNum);
      } else {
        setError(data.status || "Failed to fetch enquiries");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Network error. Please try again later."
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more enquiries
  const loadMore = () => {
    if (page < totalPages) {
      fetchEnquiries(page + 1);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchEnquiries();
    }
  }, [userId]);

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "new":
        return { text: "New", color: "#4a90e2" };
      case "contacted":
        return { text: "Contacted", color: "#f5a623" };
      case "in_progress":
        return { text: "In Progress", color: "#50e3c2" };
      case "resolved":
        return { text: "Resolved", color: "#b8e986" };
      case "closed":
        return { text: "Closed", color: "#9b9b9b" };
      default:
        return { text: status, color: "#4a4a4a" };
    }
  };

  if (!userId) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Please log in to view your enquiries</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Your Enquiries</h2>
        <p className={styles.subtitle}>
          {totalEnquiries} enquiry{totalEnquiries !== 1 ? "s" : ""} in total
        </p>
      </div>

      {loading ? (
        <div className={styles.loadingContainer}>
          <ClockLoader color="#3a7bd5" size={60} />
          <p>Loading your enquiries...</p>
        </div>
      ) : error ? (
        <div className={styles.error}>
          {error}
          <button onClick={() => fetchEnquiries(1)}>Retry</button>
        </div>
      ) : enquiries.length === 0 ? (
        <div className={styles.noResults}>
          <div className={styles.noResultsIcon}>📋</div>
          <h3>No enquiries found</h3>
          <p>You haven't submitted any enquiries yet.</p>
        </div>
      ) : (
        <>
          <div className={styles.enquiriesList}>
            {enquiries.map((enquiry) => (
              <div key={enquiry._id} className={styles.enquiryCard}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.enquiryTitle}>Enquiry</h3>
                  <div
                    className={styles.statusBadge}
                    style={{
                      backgroundColor: getStatusDisplay(enquiry.status).color,
                    }}
                  >
                    {getStatusDisplay(enquiry.status).text}
                  </div>
                </div>

                <div className={styles.cardBody}>
                  {/* --- Products/Series Loop --- */}
                  <div className={styles.itemsList}>
                    {enquiry.enquiryItems.map((item, index) => (
                      <div key={index} className={styles.itemBlock}>
                        {
                          // @ts-ignore
                          item.productId?.picture[0]?.path ? (
                            <div className={styles.productImage}>
                              <img
                                // @ts-ignore
                                src={item?.productId?.picture[0]?.path}
                                alt={item.productId.name}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src =
                                    "/img-placeholder.png";
                                }}
                              />
                            </div>
                          ) : (
                            <div className={styles.imagePlaceholder}>
                              <span>📦</span>
                            </div>
                          )
                        }

                        <div>
                          <h4 className={styles.productName}>
                            {item.seriesId?.title} - {item.productId?.name}
                          </h4>
                          <p className={styles.productId}>
                            Product ID: {item.productId?.productId}
                          </p>
                          <p className={styles.detailValue}>
                            Quantity: {item.quantity || "Not specified"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* --- Meta Info --- */}
                  <div className={styles.enquiryDetails}>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Submitted:</span>
                      <span className={styles.detailValue}>
                        {format(
                          new Date(enquiry.createdAt),
                          "MMM dd, yyyy hh:mm a"
                        )}
                      </span>
                    </div>
                    <div className={styles.detailItem}>
                      <span className={styles.detailLabel}>Message:</span>
                      <p className={styles.message}>{enquiry.message}</p>
                    </div>
                  </div>

                  {/* --- Admin Response --- */}
                  {enquiry.reply && (
                    <div className={styles.responseSection}>
                      <div className={styles.responseHeader}>
                        <div className={styles.responseIcon}>💬</div>
                        <h4>Admin Response</h4>
                      </div>
                      <div className={styles.responseContent}>
                        <p>{enquiry.reply}</p>
                        {enquiry.updatedAt && (
                          <div className={styles.responseDate}>
                            Last updated:{" "}
                            {format(
                              new Date(enquiry.updatedAt),
                              "MMM dd, yyyy"
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* --- Load More --- */}
          {page < totalPages && (
            <div className={styles.loadMoreContainer}>
              <button
                className={styles.loadMoreButton}
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <>
                    <span className={styles.spinner}></span> Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
              <div className={styles.paginationInfo}>
                Showing {enquiries.length} of {totalEnquiries} enquiries
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserEnquiriesComponent;
